import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  Plus,
  Search,
  FileText,
  Copy,
  Check,
  Info,
  Pencil,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  ChevronsUpDown,
  Download,
  Handshake,
  Sparkles,
  BookOpen,
  Mail,
  HelpCircle,
  Inbox,
  ClipboardList,
  ClipboardCheck,
  Clock,
  PiggyBank,
  Users,
  Eye,
  X,
  PartyPopper,
} from "lucide-react";
import { toast } from "sonner";
import { fileService } from "@/lib/services/fileService";
import { SiteHeader } from "@/components/site-header";
import { DownloadPackageMenu, autoDownloadKey } from "@/components/report/download-package-menu";

import { SiteFooter } from "@/components/site-footer";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { AuthGate } from "@/components/auth-gate";
import { useCases, type Case, type CaseStatus } from "@/lib/cases";
import { useMyReviewsAsCases, useMyAccountCases } from "@/hooks/use-real-reviews";
import { formatClaimTimestamp, formatClaimStamp, processingElapsed } from "@/lib/claim-timestamps";
import {
  buildDashboardTotals,
  isNegotiating,
  isReportReady,
  recoveryDisplay,
  totalRecoveryTileDisplay,
  optionalCurrency,
  vendorLabel,
  IN_PROGRESS_STATUSES,
  NEGOTIATION_STATUSES,
  READY_STATUSES,
  type RecoveryDisplay,
} from "@/lib/dashboard/metrics";

import {
  activeFilterChips,
  applyReviewTableState,
  ariaSortFor,
  customRangeError,
  isDefaultTableState,
  nextSortState,
  parseReviewTableSearch,
  resultCountLabel,
  serializeReviewTableState,
  sortPresetState,
  sortPresetValue,
  CLEAR_FILTERS_LABEL,
  DATE_FILTERS,
  DATE_FILTER_LABELS,
  DEFAULT_TABLE_STATE,
  NO_MATCH_MESSAGE,
  RECOVERY_FILTERS,
  RECOVERY_FILTER_LABELS,
  SORT_PRESETS,
  STATUS_FILTERS,
  STATUS_FILTER_LABELS,
  type DateFilter,
  type RecoveryFilter,
  type ReviewTableState,
  type SortKey,
  type StatusFilter,
} from "@/lib/dashboard/review-table";


import { currency } from "@/lib/report";

import { useAuth } from "@/hooks/use-auth";
import { aliasForUserId } from "@/lib/email-intake/alias";


export const Route = createFileRoute("/dashboard")({
  // Table controls live in the URL so refresh, Back and Forward restore the
  // customer's view. Every value is validated and falls back to a default.
  validateSearch: (search: Partial<Record<string, unknown>>): Partial<ReviewTableState> =>
    parseReviewTableSearch(search),
  head: () => ({
    meta: [
      { title: "ClaimGuard | Dashboard" },
      {
        name: "description",
        content:
          "Your personal workspace for invoice reviews, professional findings reports, and full-service negotiations.",
      },
    ],
  }),
  component: () => (
    <AuthGate>
      <MyReviewsPage />
    </AuthGate>
  ),
});


/* ─────────────────────────────────────────────────────────── */
/* Page                                                        */
/* ─────────────────────────────────────────────────────────── */

function MyReviewsPage() {
  const { user } = useAuth();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/dashboard" });
  const tableState = useMemo<ReviewTableState>(() => parseReviewTableSearch(search), [search]);
  const setTableState = (patch: Partial<ReviewTableState>) => {
    const next = { ...tableState, ...patch };
    // Typing in the search box replaces the entry (no history spam); filter and
    // sort changes push, so Back/Forward restore the customer's view.
    const keys = Object.keys(patch);
    const searchOnly =
      keys.length === 1 &&
      ["q", "claimNumber", "vendorPhone", "vinLast6"].includes(keys[0]!);
    navigate({
      search: serializeReviewTableState(next) as unknown as Partial<ReviewTableState>,
      replace: searchOnly,
    });
  };
  const resetTableState = () => {
    navigate({
      search: serializeReviewTableState(
        DEFAULT_TABLE_STATE,
      ) as unknown as Partial<ReviewTableState>,
      replace: false,
    });
  };
  const demoCases = useCases();
  const {
    cases: realCases,
    loading: realLoading,
    loadingMore,
    hasMore,
    total: serverTotal,
    loadMore,
  } = useMyReviewsAsCases();
  // Summary tiles describe the WHOLE account, not the loaded page.
  const { cases: accountCases } = useMyAccountCases();
  // Signed-in users always see their own reviews (even when empty) so a new
  // account gets a proper zero-state instead of demo data.
  const cases = user ? realCases : demoCases;
  const summaryCases = user ? accountCases : demoCases;
  const paginated = Boolean(user);
  const isInitialLoading = Boolean(user) && realLoading && realCases.length === 0;

  // Every tile and the review table read the SAME buckets and the SAME
  // recovery source (see src/lib/dashboard/metrics.ts). No metric is derived
  // twice on this page.
  const totals = useMemo(() => buildDashboardTotals(summaryCases), [summaryCases]);

  // Report-ready claims belonging to the authenticated customer, newest first.
  // Used only by the green report-ready banner.
  const readyReports = useMemo(
    () =>
      cases
        .filter(isReportReady)
        .slice()
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [cases],
  );
  const negotiations = cases.filter(isNegotiating);

  return (
    <div className="min-h-dvh bg-secondary/30">
      <SiteHeader />
      <main className="container-page space-y-8 py-8 md:py-12">
        <Breadcrumbs items={[{ label: "Dashboard" }]} />
        <WelcomeHeader user={user} hasCases={cases.length > 0} />

        <ReportReadyBanner cases={readyReports} />

        {isInitialLoading ? (
          <DashboardSkeleton />
        ) : (
          <>
            <QuickSummary
              inProgress={totals.inProgress}
              ready={totals.ready}
              negotiations={totals.negotiations}
              savings={totals.totalSupportedRecovery}
              recoveryPending={totals.recoveryPending}
              recoveryCounted={totals.recoveryCounted}
            />

            <MyReviewsWorkspace
              cases={cases}
              state={tableState}
              onChange={setTableState}
              onReset={resetTableState}
              totalCount={paginated ? Math.max(serverTotal, realCases.length) : demoCases.length}
              hasMore={paginated && hasMore}
              loadingMore={loadingMore}
              onLoadMore={loadMore}
            />

            <div className="grid gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] [&>*]:min-w-0">
              <PrivateEmail />
              <HelpSupport />
            </div>

            {negotiations.length > 0 && <ExpertNegotiation cases={negotiations} />}
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-8" aria-busy="true" aria-live="polite">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-[104px] animate-pulse rounded-2xl border border-border bg-white/70"
          />
        ))}
      </div>
      <div className="h-56 animate-pulse rounded-2xl border border-border bg-white/70" />
      <div className="h-96 animate-pulse rounded-2xl border border-border bg-white/70" />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Success banner (shown once per ready report)                */
/* ─────────────────────────────────────────────────────────── */

const BANNER_KEY = "dashboard.reportReadyDismissed";

function readDismissed(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    return new Set(JSON.parse(window.localStorage.getItem(BANNER_KEY) || "[]"));
  } catch {
    return new Set();
  }
}

function ReportReadyBanner({ cases }: { cases: Case[] }) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  useEffect(() => {
    setDismissed(readDismissed());
  }, []);
  const target = cases.find((c) => !dismissed.has(c.id));
  if (!target) return null;

  const dismiss = () => {
    // Dismiss every currently ready report, not just the featured one: otherwise the
    // next ready claim immediately renders an identical banner and the X looks inert.
    const next = new Set(dismissed);
    for (const c of cases) next.add(c.id);
    setDismissed(next);
    try {
      window.localStorage.setItem(BANNER_KEY, JSON.stringify([...next]));
    } catch {
      /* noop */
    }
  };


  return (
    <section
      className="animate-fade-in relative overflow-hidden rounded-2xl border border-savings/30 bg-gradient-to-r from-savings-soft via-white to-savings-soft/60 p-5 shadow-card md:p-6"
      role="status"
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-savings text-savings-foreground">
            <PartyPopper className="h-5 w-5" />
          </div>
          <div>
            <div className="font-display text-lg text-navy">
              Great news — your professional findings report is ready.
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Open it now to see every issue we found and your Total Supported Recovery.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 md:shrink-0">
          <Link
            to="/report/$id"
            params={{ id: target.id }}
            className="inline-flex items-center gap-2 rounded-lg bg-savings px-4 py-2.5 text-sm font-semibold text-savings-foreground shadow-sm transition hover:-translate-y-0.5"
          >
            Open Report <ArrowRight className="h-4 w-4" />
          </Link>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            className="relative z-10 inline-flex min-h-11 min-w-11 items-center justify-center rounded-md p-2 text-muted-foreground transition hover:bg-white hover:text-navy"
          >
            <X className="pointer-events-none h-4 w-4" />
          </button>

        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Welcome header                                              */
/* ─────────────────────────────────────────────────────────── */

function WelcomeHeader({
  user,
  hasCases,
}: {
  user: { email?: string | null; user_metadata?: Record<string, unknown> } | null;
  hasCases: boolean;
}) {
  void hasCases;
  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  }, []);

  const displayName = useMemo(() => {
    if (!user) return "there";
    const meta = (user.user_metadata ?? {}) as Record<string, unknown>;
    const full = typeof meta.full_name === "string" ? meta.full_name : "";
    const first = full.trim().split(/\s+/)[0];
    if (first) return first;
    const nameMeta = typeof meta.name === "string" ? meta.name.trim().split(/\s+/)[0] : "";
    if (nameMeta) return nameMeta;
    const email = user.email ?? "";
    const prefix = email.split("@")[0] ?? "";
    if (prefix) return prefix.charAt(0).toUpperCase() + prefix.slice(1);
    return "there";
  }, [user]);

  return (
    <section className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
      <div className="min-w-0">
        <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-foreground">
          Dashboard
        </div>
        <h1 className="mt-3 font-display text-4xl leading-tight text-navy md:text-5xl">
          {greeting}, {displayName}.
        </h1>
        <p className="mt-3 max-w-xl text-muted-foreground">
          Manage your invoice reviews, reports, negotiations, and savings.
        </p>
      </div>
      <Link
        to="/submit"
        className="group inline-flex items-center gap-2 self-start rounded-lg bg-savings px-5 py-3 text-sm font-semibold text-savings-foreground shadow-sm transition hover:-translate-y-0.5 hover:shadow-elevated md:self-end"
      >
        <Plus className="h-4 w-4" />
        Submit New Invoice
      </Link>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Continue where you left off                                 */
/* ─────────────────────────────────────────────────────────── */

const stepMap: Record<CaseStatus, { label: string; step: number; total: number }> = {
  awaiting_payment: { label: "Complete Submission", step: 1, total: 7 },
  received: { label: "Complete Submission", step: 1, total: 7 },
  reviewing: { label: "Reading Invoice", step: 2, total: 7 },
  report_ready: { label: "Report Ready", step: 7, total: 7 },
  self_serve: { label: "Report Ready", step: 7, total: 7 },
  negotiating: { label: "Negotiation In Progress", step: 7, total: 7 },
  waiting_vendor: { label: "Negotiation In Progress", step: 7, total: 7 },
  offer_received: { label: "Negotiation In Progress", step: 7, total: 7 },
  completed: { label: "Negotiation Complete", step: 7, total: 7 },
  action_required: { label: "Action Required", step: 2, total: 7 },
  awaiting_customer: { label: "Awaiting Customer", step: 2, total: 7 },
  pipeline_blocked: { label: "Review Needs Attention", step: 2, total: 7 },
};

type SavingsView = RecoveryDisplay;

/**
 * Every recovery figure on this page comes from the canonical dashboard
 * metrics module, so the summary tile, the table and the featured card can
 * never disagree. Wording is customer-facing and legally accurate.
 */
function savingsDisplay(c: Case): SavingsView {
  return recoveryDisplay(c);
}

/* ─────────────────────────────────────────────────────────── */
/* Quick summary                                               */
/* ─────────────────────────────────────────────────────────── */

function QuickSummary({
  inProgress,
  ready,
  negotiations,
  savings,
  recoveryPending,
  recoveryCounted,
}: {
  inProgress: number;
  ready: number;
  negotiations: number;
  savings: number;
  recoveryPending: number;
  recoveryCounted: number;
}) {
  return (
    <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <SummaryCard
        icon={Clock}
        label="Reviews In Progress"
        value={String(inProgress)}
        tone="trust"
      />
      <SummaryCard
        icon={ClipboardCheck}
        label="Reports Ready"
        value={String(ready)}
        tone="savings"
        highlight={ready > 0}
      />
      <SummaryCard
        icon={Users}
        label="Full-Service Negotiations"
        value={String(negotiations)}
        tone="negotiate"
      />
      <RecoveryTile
        savings={savings}
        recoveryPending={recoveryPending}
        recoveryCounted={recoveryCounted}
      />
    </section>
  );
}

/**
 * Total Supported Recovery tile. Presentation only — the sum arrives already
 * computed from the canonical dashboard metrics module.
 */
function RecoveryTile({
  savings,
  recoveryPending,
  recoveryCounted,
}: {
  savings: number;
  recoveryPending: number;
  recoveryCounted: number;
}) {
  const display = totalRecoveryTileDisplay({
    totalSupportedRecovery: savings,
    recoveryCounted,
    recoveryPending,
  });
  return (
    <SummaryCard
      icon={PiggyBank}
      label="Total Supported Recovery"
      value={display.value}
      tone="savings"
      muted={recoveryCounted === 0}
      note={display.note}
    />
  );
}

function SummaryCard({
  icon: Icon,
  label,
  value,
  tone,
  highlight,
  note,
  muted,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  tone: "trust" | "savings" | "negotiate";
  highlight?: boolean;
  note?: string;
  /** Render the value as a muted status phrase rather than a headline figure. */
  muted?: boolean;
}) {
  const iconCls =
    tone === "savings"
      ? "bg-savings-soft text-savings"
      : tone === "negotiate"
        ? "bg-negotiate-soft text-negotiate"
        : "bg-trust-soft text-trust";
  const valueCls =
    tone === "savings" ? "text-savings" : tone === "negotiate" ? "text-negotiate" : "text-navy";
  const ringCls = highlight ? "ring-2 ring-savings/40 animate-fade-in" : "";
  return (
    <div
      className={`group flex items-start gap-4 rounded-2xl border border-border bg-white p-6 shadow-card transition hover:-translate-y-0.5 hover:shadow-elevated ${ringCls}`}
    >
      <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${iconCls}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div
          className={
            muted
              ? "mt-1 font-display text-lg leading-tight text-muted-foreground"
              : `mt-1 font-display text-3xl leading-none ${valueCls}`
          }
        >
          {value}
        </div>

        {note && (
          <div className="mt-1.5 text-[11px] leading-snug text-muted-foreground">{note}</div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* My Reviews workspace                                        */
/* ─────────────────────────────────────────────────────────── */

const CONTROL_CLASS =
  "h-10 min-h-11 rounded-lg border border-border bg-white px-3 text-sm text-navy focus:border-trust focus:outline-none focus:ring-2 focus:ring-trust/20";

const SORTABLE_COLUMNS: { key: Exclude<SortKey, "default">; label: string }[] = [
  { key: "file", label: "File Number" },
  { key: "vendor", label: "Vendor" },
  { key: "status", label: "Status" },
  { key: "submitted", label: "Submitted" },
  { key: "recovery", label: "Total Supported Recovery" },
];

/** Compact labelled dropdown used by every control in the reviews toolbar. */
function ToolbarSelect({
  id,
  label,
  value,
  onChange,
  options,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="flex min-w-0 items-center gap-2 lg:w-[15.5rem]">
      <label
        htmlFor={id}
        className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-muted-foreground"
      >
        {label}
      </label>
      <select
        id={id}
        className={`${CONTROL_CLASS} min-w-0 flex-1`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}


function SortHeader({
  column,
  label,
  state,
  onSort,
  align = "left",
}: {
  column: Exclude<SortKey, "default">;
  label: string;
  state: ReviewTableState;
  onSort: (column: Exclude<SortKey, "default">) => void;
  align?: "left" | "right";
}) {
  const sortState = ariaSortFor(state, column);
  const active = sortState !== "none";
  const next = nextSortState(state, column);
  const nextLabel =
    next.sort === "default"
      ? "restore the default newest-first order"
      : next.direction === "asc"
        ? "sort ascending"
        : "sort descending";
  const Icon = !active ? ChevronsUpDown : sortState === "ascending" ? ArrowUp : ArrowDown;
  return (
    <th className="px-5 py-3" aria-sort={sortState} scope="col">
      <button
        type="button"
        onClick={() => onSort(column)}
        aria-label={`${label}: ${nextLabel}`}
        className={`inline-flex w-full min-h-9 items-center gap-1.5 rounded-md px-1 py-1 text-[10px] font-bold uppercase tracking-wider transition hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-trust focus-visible:ring-offset-1 ${
          align === "right" ? "justify-end" : "justify-start"
        } ${active ? "text-navy" : "text-muted-foreground"}`}
      >
        <span>{label}</span>
        <Icon className={`h-3.5 w-3.5 shrink-0 ${active ? "opacity-100" : "opacity-50"}`} />
      </button>
    </th>
  );
}

/**
 * Debounced text filter. Typing stays instant locally; the URL search state is
 * only updated once the customer pauses, so history stays clean.
 */
function DebouncedFilterInput({
  id,
  label,
  value,
  onCommit,
  placeholder,
  inputMode,
  maxLength,
}: {
  id: string;
  label: string;
  value: string;
  onCommit: (v: string) => void;
  placeholder?: string;
  inputMode?: "text" | "tel" | "numeric";
  maxLength?: number;
}) {
  const [draft, setDraft] = useState(value);
  const committed = useRef(value);
  useEffect(() => {
    if (value !== committed.current) {
      committed.current = value;
      setDraft(value);
    }
  }, [value]);
  useEffect(() => {
    if (draft === committed.current) return;
    const t = window.setTimeout(() => {
      committed.current = draft;
      onCommit(draft);
    }, 300);
    return () => window.clearTimeout(t);
  }, [draft, onCommit]);

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <label
        htmlFor={id}
        className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground"
      >
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={draft}
        inputMode={inputMode}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        className="h-10 w-full min-w-0 rounded-lg border border-border bg-white px-3 text-sm text-navy placeholder:text-muted-foreground focus:border-trust focus:outline-none focus:ring-2 focus:ring-trust/20"
      />
    </div>
  );
}

function MyReviewsWorkspace({
  cases,
  state,
  onChange,
  onReset,
  totalCount,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
}: {
  cases: Case[];
  state: ReviewTableState;
  onChange: (patch: Partial<ReviewTableState>) => void;
  onReset: () => void;
  totalCount?: number;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
}) {
  const [queryDraft, setQueryDraft] = useState(state.q);
  useEffect(() => {
    setQueryDraft(state.q);
  }, [state.q]);

  const dateError = useMemo(
    () => (state.date === "custom" ? customRangeError(state.start, state.end) : null),
    [state.date, state.start, state.end],
  );

  const filtered = useMemo(() => applyReviewTableState(cases, state), [cases, state]);
  const atDefault = isDefaultTableState(state);
  const chips = useMemo(() => activeFilterChips(state), [state]);

  const handleSort = (column: Exclude<SortKey, "default">) => onChange(nextSortState(state, column));

  return (
    <section>
      <div className="mb-4">
        <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-foreground">
          Workspace
        </div>
        <h2 className="mt-2 font-display text-2xl text-navy md:text-3xl">Your Reviews</h2>
      </div>

      <div className="rounded-2xl border border-border bg-white shadow-card">
        <div className="space-y-3 border-b border-border p-4">
          {/* One toolbar row: search, four dropdowns, live result count. Wraps
              cleanly at tablet width and stacks on mobile. */}
          <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center">
            <div className="relative min-w-0 flex-1 lg:min-w-[16rem] lg:flex-none">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={queryDraft}
                onChange={(e) => {
                  setQueryDraft(e.target.value);
                  onChange({ q: e.target.value });
                }}
                placeholder="Search file # or vendor"
                aria-label="Search your reviews by file number or vendor"
                className="h-10 w-full rounded-lg border border-border bg-white pl-9 pr-3 text-sm text-navy placeholder:text-muted-foreground focus:border-trust focus:outline-none focus:ring-2 focus:ring-trust/20"
              />
            </div>

            <ToolbarSelect
              id="reviews-status-filter"
              label="Status"
              value={state.status}
              onChange={(v) => onChange({ status: v as StatusFilter })}
              options={STATUS_FILTERS.map((k) => ({ value: k, label: STATUS_FILTER_LABELS[k] }))}
            />

            <ToolbarSelect
              id="reviews-recovery-filter"
              label="Recovery"
              value={state.recovery}
              onChange={(v) => onChange({ recovery: v as RecoveryFilter })}
              options={RECOVERY_FILTERS.map((k) => ({
                value: k,
                label: RECOVERY_FILTER_LABELS[k],
              }))}
            />

            <ToolbarSelect
              id="reviews-date-filter"
              label="Date"
              value={state.date}
              onChange={(v) => onChange({ date: v as DateFilter })}
              options={DATE_FILTERS.map((k) => ({ value: k, label: DATE_FILTER_LABELS[k] }))}
            />

            <ToolbarSelect
              id="reviews-sort"
              label="Sort"
              value={sortPresetValue({ sort: state.sort, direction: state.direction })}
              onChange={(v) => onChange(sortPresetState(v))}
              options={SORT_PRESETS.map((o) => ({ value: o.value, label: o.label }))}
            />

            <p
              className="text-xs text-muted-foreground lg:ml-auto"
              role="status"
              aria-live="polite"
            >
              {resultCountLabel(
                filtered.length,
                totalCount ?? cases.length,
                !atDefault || filtered.length !== (totalCount ?? cases.length),
              )}
            </p>
          </div>

          {/* Second row: precise lookups. Wraps naturally on tablet/mobile. */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <DebouncedFilterInput
              id="reviews-claim-number-filter"
              label="Claim #"
              placeholder="Search by claim number"
              value={state.claimNumber}
              onCommit={(v) => onChange({ claimNumber: v })}
            />
            <DebouncedFilterInput
              id="reviews-vendor-phone-filter"
              label="Vendor Phone #"
              placeholder="Search by vendor phone"
              inputMode="tel"
              value={state.vendorPhone}
              onCommit={(v) => onChange({ vendorPhone: v })}
            />
            <DebouncedFilterInput
              id="reviews-vin-filter"
              label="Last 6 of VIN"
              placeholder="Search last 6 of VIN"
              maxLength={17}
              value={state.vinLast6}
              onCommit={(v) => onChange({ vinLast6: v })}
            />
          </div>

          {state.date === "custom" && (
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="reviews-date-start"
                  className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground"
                >
                  Start Date
                </label>
                <input
                  id="reviews-date-start"
                  type="date"
                  className={CONTROL_CLASS}
                  value={state.start}
                  aria-invalid={Boolean(dateError)}
                  aria-describedby={dateError ? "reviews-date-error" : undefined}
                  onChange={(e) => onChange({ start: e.target.value })}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="reviews-date-end"
                  className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground"
                >
                  End Date
                </label>
                <input
                  id="reviews-date-end"
                  type="date"
                  className={CONTROL_CLASS}
                  value={state.end}
                  aria-invalid={Boolean(dateError)}
                  aria-describedby={dateError ? "reviews-date-error" : undefined}
                  onChange={(e) => onChange({ end: e.target.value })}
                />
              </div>
            </div>
          )}

          {dateError && (
            <p id="reviews-date-error" role="alert" className="text-xs font-medium text-destructive">
              {dateError}
            </p>
          )}

          {(chips.length > 0 || !atDefault) && (
            <div className="flex flex-wrap items-center gap-2">
              {chips.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => onChange(chip.clear)}
                  aria-label={`Remove filter ${chip.label}`}
                  className="inline-flex min-h-8 items-center gap-1.5 rounded-full border border-border bg-secondary/50 px-3 py-1 text-xs font-semibold text-navy transition hover:border-trust/40 hover:text-trust focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-trust focus-visible:ring-offset-2"
                >
                  <span className="max-w-[16rem] truncate">{chip.label}</span>
                  <X className="h-3 w-3 shrink-0" />
                </button>
              ))}
              {!atDefault && (
                <button
                  type="button"
                  onClick={onReset}
                  className="inline-flex min-h-8 items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-semibold text-trust underline-offset-2 transition hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-trust focus-visible:ring-offset-2"
                >
                  {CLEAR_FILTERS_LABEL}
                </button>
              )}
            </div>
          )}
        </div>



        {filtered.length === 0 ? (
          cases.length === 0 ? (
            <EmptyState
              icon={Inbox}
              title="No reviews yet"
              body="Submit your first invoice and it will show up here."
              ctaLabel="Submit Your First Invoice"
            />
          ) : (
            <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-trust-soft text-trust">
                <Inbox className="h-6 w-6" />
              </div>
              <div className="font-display text-xl text-navy">{NO_MATCH_MESSAGE}</div>
              <button
                type="button"
                onClick={onReset}
                className="mt-2 inline-flex min-h-11 items-center gap-2 rounded-lg bg-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-trust focus-visible:ring-offset-2"
              >
                {CLEAR_FILTERS_LABEL}
              </button>
            </div>
          )
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-secondary/40 text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    {SORTABLE_COLUMNS.map((col) => (
                      <SortHeader
                        key={col.key}
                        column={col.key}
                        label={col.label}
                        state={state}
                        onSort={handleSort}
                      />
                    ))}
                    <th className="px-5 py-3" scope="col">
                      Current Step
                    </th>
                    <th className="px-5 py-3 text-right" scope="col">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((c) => {
                    const step = stepMap[c.status];
                    const sv = savingsDisplay(c);
                    const savingsCls =
                      sv.tone === "muted"
                        ? "text-muted-foreground italic font-normal"
                        : "text-savings";
                    return (
                      <tr key={c.id} className="transition hover:bg-secondary/30">
                        <td className="px-5 py-4 font-mono text-xs font-semibold text-navy">
                          {c.id}
                        </td>
                        <td className="px-5 py-4 text-navy">
                          {vendorLabel(c).identified ? (
                            vendorLabel(c).text
                          ) : (
                            <span className="italic text-muted-foreground">
                              {vendorLabel(c).text}
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <CaseStatusChip status={c.status} />
                        </td>
                        <td className="whitespace-nowrap px-5 py-4 text-muted-foreground">
                          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                            Submitted
                          </div>
                          <div className="text-xs font-medium text-navy">
                            {formatClaimStamp(c.timestamps?.submittedAt ?? c.createdAt)}
                          </div>
                          {c.timestamps && processingElapsed(c.timestamps) && (
                            <>
                              <div className="mt-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                                Processing Time
                              </div>
                              <div className="text-xs font-medium text-navy">
                                {processingElapsed(c.timestamps)}
                              </div>
                            </>
                          )}
                        </td>
                        <td className={`px-5 py-4 font-semibold ${savingsCls}`}>
                          <div className="whitespace-nowrap">{sv.value}</div>
                          {sv.label !== "Total Supported Recovery" && (
                            <div className="text-[11px] font-medium not-italic leading-snug text-muted-foreground">
                              {sv.label}
                            </div>
                          )}
                          {sv.note && (
                            <div className="mt-0.5 max-w-[15rem] text-[10px] font-normal not-italic leading-snug text-muted-foreground/80">
                              {sv.note}
                            </div>
                          )}
                        </td>

                        <td className="px-5 py-4 text-sm text-muted-foreground">{step.label}</td>
                        <td className="px-5 py-4">
                          <RowActions c={c} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="divide-y divide-border md:hidden">
              {filtered.map((c) => {
                const step = stepMap[c.status];
                const sv = savingsDisplay(c);
                const savingsCls =
                  sv.tone === "muted" ? "text-muted-foreground italic" : "text-savings";
                return (
                  <div key={c.id} className="p-5">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-mono text-xs font-semibold text-navy">{c.id}</span>
                      <CaseStatusChip status={c.status} />
                    </div>
                    <div className="mt-3 font-semibold text-navy">
                      {vendorLabel(c).identified ? vendorLabel(c).text : `Invoice ${c.id}`}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {step.label} · Submitted{" "}
                      {formatClaimTimestamp(c.timestamps?.submittedAt ?? c.createdAt)}
                    </div>
                    <div className={`mt-3 text-sm font-semibold ${savingsCls}`}>{sv.value}</div>
                    {sv.label !== "Total Supported Recovery" && (
                      <div className="mt-0.5 text-[11px] font-medium not-italic leading-snug text-muted-foreground">
                        {sv.label}
                      </div>
                    )}
                    {sv.note && (
                      <div className="mt-0.5 text-[10px] font-normal not-italic leading-snug text-muted-foreground/80">
                        {sv.note}
                      </div>
                    )}

                    <div className="mt-4">
                      <RowActions c={c} />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
      {hasMore && onLoadMore && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={loadingMore}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-white px-4 py-2 text-sm font-semibold text-navy transition hover:border-trust/40 hover:text-trust disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loadingMore ? "Loading…" : "Show more reviews"}
          </button>
        </div>
      )}
    </section>
  );
}


function ViewInvoiceButton({ c, size = "sm" }: { c: Case; size?: "sm" | "md" }) {
  const [busy, setBusy] = useState(false);
  const cls =
    size === "md"
      ? "inline-flex items-center gap-2 rounded-lg border border-border bg-white px-4 py-2.5 text-sm font-semibold text-navy transition hover:border-trust/40 hover:text-trust disabled:opacity-60"
      : "inline-flex min-h-9 items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-navy transition hover:border-trust/40 hover:text-trust disabled:opacity-60";
  const iconCls = size === "md" ? "h-4 w-4" : "h-3.5 w-3.5";

  async function openInvoice() {
    if (busy) return;
    const primary = c.primaryInvoice;
    if (!primary) {
      toast.info("Original invoice is not available for this review.");
      return;
    }
    setBusy(true);
    try {
      const url = await fileService.signedUrl(primary.bucket, primary.storage_path, 600);
      window.open(url, "_blank", "noopener,noreferrer");
      if (primary.total_files > 1) {
        toast.message(
          `Opened primary invoice — ${primary.total_files - 1} additional document${primary.total_files - 1 === 1 ? "" : "s"} available in the report appendix.`,
        );
      }
    } catch {
      toast.error("Couldn't open the invoice. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  const disabled = !c.primaryInvoice;
  return (
    <button
      type="button"
      onClick={openInvoice}
      disabled={busy || disabled}
      title={
        disabled
          ? "Original invoice is not available for this review."
          : "Open the uploaded invoice"
      }
      className={cls}
    >
      <Eye className={iconCls} />
      {busy ? "Opening…" : "View Invoice"}
    </button>
  );
}

function RowActions({ c }: { c: Case }) {
  const navigate = useNavigate();
  const ready = ["report_ready", "self_serve", "completed"].includes(c.status);
  const negotiating = ["negotiating", "offer_received"].includes(c.status);

  if (ready) {
    return (
      <div className="flex flex-col items-end gap-2">
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Link
            to="/report/$id"
            params={{ id: c.id }}
            className="inline-flex min-h-9 items-center gap-1.5 rounded-md bg-navy px-3 py-1.5 text-xs font-semibold text-white transition hover:opacity-90"
          >
            Open Report
          </Link>
          <DownloadPackageMenu
            triggerAriaLabel={`Download PDF for ${c.id} — choose a package`}
            idleLabel="Download PDF"
            iconClassName="h-3.5 w-3.5"
            chevronClassName="h-3 w-3"
            triggerClassName="inline-flex min-h-9 items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-navy transition hover:border-trust/40 hover:text-trust"
            onSelect={(pkg) => {
              try {
                window.sessionStorage.setItem(autoDownloadKey(c.id), pkg);
              } catch {
                /* private mode — the search param below still carries the choice */
              }
              navigate({ to: "/report/$id", params: { id: c.id }, search: { download: pkg } });
            }}

          />

        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Link
            to="/review"
            search={{ id: c.id }}
            aria-label={`Open the case overview for ${c.id}`}
            className="inline-flex min-h-9 items-center gap-1.5 rounded-md border border-navy/30 px-3 py-1.5 text-xs font-semibold text-navy transition hover:border-trust/40 hover:text-trust"
          >
            Case Overview
          </Link>
          <ViewInvoiceButton c={c} />
          {!c.negotiationChosen && (
            <button
              type="button"
              onClick={() => navigate({ to: "/negotiation", search: { review: c.id } })}
              className="inline-flex min-h-9 items-center gap-1.5 rounded-md bg-negotiate px-3 py-1.5 text-xs font-semibold text-white transition hover:opacity-90"
            >
              Request Expert
            </button>
          )}
        </div>
      </div>
    );
  }


  if (negotiating) {
    return (
      <div className="flex flex-wrap items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => navigate({ to: "/negotiation", search: { review: c.id } })}
          className="inline-flex min-h-9 items-center gap-1.5 rounded-md bg-negotiate px-3 py-1.5 text-xs font-semibold text-white transition hover:opacity-90"
        >
          View Negotiation
        </button>
        <ViewInvoiceButton c={c} />
      </div>
    );
  }

  /* Awaiting payment means the submission is still a draft: reopen the exact
     saved submission on the Submit page instead of starting a new one. */
  if (c.status === "awaiting_payment" || c.status === "received") {
    return (
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Link
          to="/submit"
          search={{ claim: c.id }}
          className="inline-flex min-h-9 items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-navy transition hover:border-trust/40 hover:text-trust"
        >
          Complete Submission
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
        <ViewInvoiceButton c={c} />
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <Link
        to="/review"
        search={{ id: c.id }}
        className="inline-flex min-h-9 items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-navy transition hover:border-trust/40 hover:text-trust"
      >
        Continue Review
        <ArrowRight className="h-3.5 w-3.5" />
      </Link>
      <ViewInvoiceButton c={c} />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Private submission email                                    */
/* ─────────────────────────────────────────────────────────── */

function PrivateEmail() {
  const { user } = useAuth();
  const address = aliasForUserId(user?.id);
  const [copied, setCopied] = useState(false);
  return (
    <section>
      <div className="rounded-2xl border border-border bg-white p-6 shadow-card md:p-7">
        <div className="flex items-start gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-trust-soft text-trust">
            <Mail className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2">
              <div className="min-w-0 font-display text-lg text-navy sm:text-xl">
                Your Private Submission Email
              </div>
              <Info className="h-4 w-4 shrink-0 text-muted-foreground" />
            </div>

            <p className="mt-1 text-sm text-muted-foreground">
              Forward invoices here anytime and we'll automatically add them to your account.
            </p>

            <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-secondary/40 p-3">
              <code className="min-w-0 flex-1 truncate font-mono text-xs text-navy sm:text-sm">
                {address}
              </code>

              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(address);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                }}
                className="inline-flex min-h-9 items-center gap-1.5 rounded-md bg-navy px-3 py-1.5 text-xs font-semibold text-white transition hover:opacity-90"
              >
                {copied ? (
                  <>
                    <Check className="h-3.5 w-3.5" /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" /> Copy
                  </>
                )}
              </button>
            </div>

            <button
              type="button"
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-trust hover:underline"
            >
              <Pencil className="h-3.5 w-3.5" /> Change Address
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Help & Support                                              */
/* ─────────────────────────────────────────────────────────── */

/** The one verified ClaimGuard support mailbox (also published on /support). */
const SUPPORT_EMAIL = "support@invoiceintelligence.com";
const SUPPORT_MAILTO = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
  "ClaimGuard Customer Support Request",
)}`;

const HELP_ROW_CLASS =
  "group flex min-h-12 w-full items-center gap-3 rounded-lg border border-transparent p-3 text-left transition hover:border-border hover:bg-secondary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-trust focus-visible:ring-offset-2 cursor-pointer";

function HelpRowBody({
  icon: Icon,
  label,
  body,
}: {
  icon: typeof BookOpen;
  label: string;
  body: string;
}) {
  return (
    <>
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary text-navy">
        <Icon className="h-4 w-4" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-navy">{label}</div>
        <div className="text-xs text-muted-foreground">{body}</div>
      </div>
      <ArrowRight
        className="h-4 w-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-trust"
        aria-hidden
      />
    </>
  );
}

function HelpSupport() {
  return (
    <section>
      <div className="rounded-2xl border border-border bg-white p-6 shadow-card md:p-7">
        <div className="flex items-center gap-2">
          <div className="font-display text-xl text-navy">Help &amp; Support</div>
        </div>
        <ul className="mt-5 space-y-2">
          <li>
            <Link to="/support" className={HELP_ROW_CLASS}>
              <HelpRowBody
                icon={BookOpen}
                label="Knowledge Base"
                body="Guides & how-tos on the Support Center"
              />
            </Link>
          </li>
          <li>
            <a href={SUPPORT_MAILTO} className={HELP_ROW_CLASS}>
              <HelpRowBody
                icon={Mail}
                label="Email Support"
                body="We reply within 1 business day"
              />
            </a>
          </li>
          <li>
            <Link to="/" hash="faq" className={HELP_ROW_CLASS}>
              <HelpRowBody icon={HelpCircle} label="FAQ" body="Common questions answered" />
            </Link>
          </li>
        </ul>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Expert negotiation                                          */
/* ─────────────────────────────────────────────────────────── */

function ExpertNegotiation({ cases }: { cases: Case[] }) {
  const navigate = useNavigate();
  return (
    <section>
      <div className="mb-4 flex items-end justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-negotiate">
            Full-Service Negotiation
          </div>
          <h2 className="mt-2 font-display text-2xl text-navy md:text-3xl">
            Handled by our specialists
          </h2>
        </div>
      </div>

      {cases.length === 0 ? (
        <div className="rounded-2xl border border-border bg-gradient-to-br from-white to-negotiate-soft/40 p-8 shadow-card md:p-10">
          <div className="flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-negotiate-soft text-negotiate">
                <Handshake className="h-6 w-6" />
              </div>
              <div>
                <div className="font-display text-xl text-navy">No negotiations in progress</div>
                <p className="mt-1 max-w-lg text-sm text-muted-foreground">
                  When you're ready, our specialists can negotiate directly with the vendor on your
                  behalf — from letter to resolution.
                </p>
              </div>
            </div>
            <Link
              to="/submit"
              className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-negotiate px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
            >
              <Sparkles className="h-4 w-4" />
              Request Expert Help
            </Link>
          </div>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {cases.map((c) => {
            const sv = savingsDisplay(c);
            return (
              <div
                key={c.id}
                className="rounded-2xl border border-negotiate/25 bg-white p-6 shadow-card transition hover:-translate-y-0.5 hover:shadow-elevated"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="rounded-md bg-negotiate/10 px-2.5 py-1 font-mono text-[11px] font-semibold text-negotiate">
                    {c.id}
                  </span>
                  <CaseStatusChip status={c.status} />
                </div>
                <div
                  className={`mt-4 font-display text-xl ${vendorLabel(c).identified ? "text-navy" : "italic text-muted-foreground"}`}
                >
                  {vendorLabel(c).text}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Assigned Specialist
                    </div>
                    <div className="mt-1 text-navy">Alex Morgan</div>
                  </div>
                  <div>
                    <div
                      className={`font-semibold ${sv.tone === "savings" ? "text-savings" : "text-muted-foreground"}`}
                    >
                      {sv.value}
                    </div>
                    <div className="mt-0.5 text-[11px] font-medium leading-snug text-muted-foreground">
                      {sv.label}
                    </div>
                  </div>

                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Latest Update
                    </div>
                    <div className="mt-1 text-navy">Offer requested from vendor</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Timeline
                    </div>
                    <div className="mt-1 text-navy">
                      Started {formatClaimTimestamp(c.timestamps?.submittedAt ?? c.createdAt)}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => navigate({ to: "/negotiation", search: { review: c.id } })}
                  className="mt-5 inline-flex items-center gap-2 rounded-lg bg-negotiate px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
                >
                  View Negotiation
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Shared helpers                                              */
/* ─────────────────────────────────────────────────────────── */

function EmptyState({
  icon: Icon,
  title,
  body,
  ctaLabel,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  body: string;
  ctaLabel?: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-trust-soft text-trust">
        <Icon className="h-6 w-6" />
      </div>
      <div className="font-display text-xl text-navy">{title}</div>
      <p className="max-w-md text-sm text-muted-foreground">{body}</p>
      {ctaLabel && (
        <Link
          to="/submit"
          className="mt-2 inline-flex items-center gap-2 rounded-lg bg-savings px-4 py-2.5 text-sm font-semibold text-savings-foreground shadow-sm transition hover:-translate-y-0.5"
        >
          {ctaLabel}
        </Link>
      )}
    </div>
  );
}

const statusChipMap: Record<CaseStatus, { label: string; cls: string }> = {
  awaiting_payment: { label: "Needs Required Details", cls: "bg-fasttrack-soft text-fasttrack" },
  received: { label: "Needs Required Details", cls: "bg-fasttrack-soft text-fasttrack" },
  reviewing: { label: "Reviewing", cls: "bg-trust-soft text-trust" },
  report_ready: { label: "✓ Findings Ready", cls: "bg-savings-soft text-savings" },
  self_serve: { label: "✓ Findings Ready", cls: "bg-savings-soft text-savings" },
  negotiating: { label: "Vendor Negotiation Active", cls: "bg-negotiate-soft text-negotiate" },
  waiting_vendor: { label: "Waiting on Vendor", cls: "bg-fasttrack-soft text-fasttrack" },
  offer_received: { label: "Offer Received", cls: "bg-negotiate-soft text-negotiate" },
  completed: { label: "Review Complete", cls: "bg-savings-soft text-savings" },
  action_required: { label: "Action Required", cls: "bg-destructive/10 text-destructive" },
  awaiting_customer: { label: "Awaiting Customer", cls: "bg-fasttrack-soft text-fasttrack" },
  pipeline_blocked: { label: "Review Needs Attention", cls: "bg-fasttrack-soft text-fasttrack" },
};

function CaseStatusChip({ status }: { status: CaseStatus }) {
  const s = statusChipMap[status];
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${s.cls}`}
    >
      {s.label}
    </span>
  );
}

/* Prevent unused import warnings for icons wired for future use. */
void ClipboardList;
void FileText;
